import { useState, useRef, useEffect, useCallback } from 'react';
import { X, QrCode, Camera, CheckCircle2, XCircle, ImagePlus, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from './ui/alert-dialog';
import { Html5Qrcode, Html5QrcodeScannerState } from 'html5-qrcode';
import { parseQRCode, isValidQRUrl, type QRParseResult } from '../../utils/qrParser';
import { chargePointService } from '../../services/chargePointService';

export interface QRScanResult {
  type: 'location' | 'chargepoint';
  locationId: string;
  chargePointId?: string;
  /** Connector the sticker is attached to, when it names one. */
  gun?: string;
}

interface QRScannerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onScanSuccess: (result: QRScanResult) => void;
}

type ScanStatus = 'idle' | 'initializing' | 'scanning' | 'processing' | 'success' | 'error';

/**
 * Side of the square scan region, as a fraction of the viewfinder's shorter edge.
 *
 * This single number drives BOTH the region html5-qrcode decodes and the frame we
 * draw on screen. They must agree: a frame larger than the decode region tells the
 * user to fill it with a QR code whose edges are then cropped away before decoding,
 * which reads as "it just won't scan". Keeping one constant makes drift impossible.
 *
 * Replaces a hardcoded 250px box, which was ~74% of the viewfinder on a 6.1" phone
 * and a much tighter target on a small one.
 */
const SCAN_BOX_RATIO = 0.72;

/** The branded viewfinder frame. Positioned against the visible container, so —
 *  unlike the library's own overlay — it cannot be clipped by overflow. */
function ScanFrame() {
  return (
    <div
      className="relative border-4 border-[#8bc43e]/50 rounded-2xl"
      style={{ width: `${SCAN_BOX_RATIO * 100}%`, aspectRatio: '1' }}
    >
      <div className="absolute -top-1 -left-1 w-12 h-12 border-t-4 border-l-4 border-[#8bc43e] rounded-tl-2xl" />
      <div className="absolute -top-1 -right-1 w-12 h-12 border-t-4 border-r-4 border-[#8bc43e] rounded-tr-2xl" />
      <div className="absolute -bottom-1 -left-1 w-12 h-12 border-b-4 border-l-4 border-[#8bc43e] rounded-bl-2xl" />
      <div className="absolute -bottom-1 -right-1 w-12 h-12 border-b-4 border-r-4 border-[#8bc43e] rounded-br-2xl" />
    </div>
  );
}

export function QRScanner({ open, onOpenChange, onScanSuccess }: QRScannerProps) {
  const [scanStatus, setScanStatus] = useState<ScanStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [showRationale, setShowRationale] = useState(false);
  // Whether a button already answered the rationale, so a dismissal handler does
  // not act on the close that the button itself caused.
  const rationaleResolvedRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scannerContainerRef = useRef<HTMLDivElement>(null);
  const isInitializedRef = useRef(false);
  const successResultRef = useRef<QRScanResult | null>(null);
  const hasGrantedCameraRef = useRef(false);
  // Re-entry gate for handleScanSuccess. The camera callback is captured once at
  // start(), so its scanStatus closure is stale and cannot stop repeated frames
  // while a guest-charger lookup is in flight. Cleared on error-reset and on close
  // (the latter also aborts an in-flight lookup).
  const isProcessingRef = useRef(false);

  // Process scanned QR code text
  const processQRCode = useCallback((decodedText: string): QRParseResult | null => {
    // Check if it looks like a URL
    if (!isValidQRUrl(decodedText)) {
      return null;
    }

    // Parse the URL
    return parseQRCode(decodedText);
  }, []);

  // Handle successful scan
  const handleScanSuccess = useCallback(async (decodedText: string) => {
    // Prevent multiple success handlers
    if (isProcessingRef.current || scanStatus === 'success' || scanStatus === 'processing') {
      return;
    }
    isProcessingRef.current = true;

    setScanStatus('processing');

    const parsed = processQRCode(decodedText);

    let result: QRScanResult | null = null;
    let errorText = 'This QR code is not recognized. Please scan an EVee Buddy charging station QR code.';

    if (parsed?.type === 'guest-charger') {
      // Physical charger sticker: resolve the human-readable charger ID to the
      // internal UUIDs the app routes need.
      try {
        const chargePoint = await chargePointService.resolveByChargerId(parsed.chargerId);
        result = {
          type: 'chargepoint',
          locationId: chargePoint.locationId,
          chargePointId: chargePoint.id,
          ...(parsed.gun ? { gun: parsed.gun } : {}),
        };
      } catch (err: any) {
        console.error('[QRScanner] Failed to resolve charger:', err);
        errorText = err?.response?.status === 404
          ? 'Charger not found. Please check the QR sticker and try again.'
          : "Couldn't reach the server. Check your connection and try again.";
      }
      // Modal was closed while the lookup was in flight — drop the result
      if (!isProcessingRef.current) {
        return;
      }
    } else if (parsed) {
      result = parsed;
    }

    if (result) {
      const scanResult = result;
      // Store the result for later use
      successResultRef.current = scanResult;
      setScanStatus('success');

      // Brief delay to show success state, then callback and close
      setTimeout(() => {
        onScanSuccess(scanResult);
        onOpenChange(false);
      }, 1000);
    } else {
      // Invalid QR code format or failed charger lookup
      setScanStatus('error');
      setErrorMessage(errorText);

      // Reset after showing error
      setTimeout(() => {
        isProcessingRef.current = false;
        setScanStatus('scanning');
        setErrorMessage('');
      }, 3000);
    }
  }, [scanStatus, processQRCode, onScanSuccess, onOpenChange]);

  // Initialize camera scanner
  const initializeScanner = useCallback(async () => {
    if (!scannerContainerRef.current || isInitializedRef.current) {
      return;
    }

    const containerId = 'qr-scanner-container';

    // Ensure container has the ID
    scannerContainerRef.current.id = containerId;

    setScanStatus('initializing');
    setPermissionDenied(false);
    setErrorMessage('');

    try {
      const html5QrCode = new Html5Qrcode(containerId);
      scannerRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          // Derived from the viewfinder rather than fixed, so the decode region
          // tracks the frame drawn over it on every screen size. See SCAN_BOX_RATIO.
          qrbox: (viewfinderWidth: number, viewfinderHeight: number) => {
            const side = Math.round(Math.min(viewfinderWidth, viewfinderHeight) * SCAN_BOX_RATIO);
            return { width: side, height: side };
          },
          // A hint only — iOS returns 4:3 regardless. The CSS in index.css is what
          // actually reconciles the video with the square container.
          aspectRatio: 1,
        },
        (decodedText) => {
          handleScanSuccess(decodedText);
        },
        () => {
          // QR code scanning in progress - ignore failures
        }
      );

      isInitializedRef.current = true;
      hasGrantedCameraRef.current = true;
      setScanStatus('scanning');
    } catch (err: any) {
      console.error('[QRScanner] Failed to start camera:', err);

      if (err?.message?.includes('Permission') || err?.name === 'NotAllowedError') {
        setPermissionDenied(true);
        setErrorMessage('Camera access denied. You can upload a QR code image instead.');
      } else if (err?.message?.includes('NotFoundError') || err?.name === 'NotFoundError') {
        setErrorMessage('No camera found on this device. Please upload a QR code image.');
      } else {
        setErrorMessage('Failed to access camera. Please try uploading a QR code image.');
      }

      setScanStatus('error');
    }
  }, [handleScanSuccess]);

  // Stop scanner
  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try {
        const state = scannerRef.current.getState();
        if (state === Html5QrcodeScannerState.SCANNING || state === Html5QrcodeScannerState.PAUSED) {
          await scannerRef.current.stop();
        }
        scannerRef.current.clear();
      } catch (err) {
        console.error('[QRScanner] Error stopping scanner:', err);
      }
      scannerRef.current = null;
    }
    isInitializedRef.current = false;
  }, []);

  // Handle rationale "Continue" — start the camera
  const handleRationaleAllow = useCallback(() => {
    rationaleResolvedRef.current = true;
    setShowRationale(false);
    // Small delay to ensure DOM is ready after dialog closes
    setTimeout(() => {
      initializeScanner();
    }, 100);
  }, [initializeScanner]);

  // Handle rationale "Upload Instead" — skip camera entirely
  const handleRationaleDeny = useCallback(() => {
    rationaleResolvedRef.current = true;
    setShowRationale(false);
    setPermissionDenied(true);
    setErrorMessage('Camera access skipped. You can upload a QR code image instead.');
    setScanStatus('error');
  }, []);

  // Keep stable refs to the latest callbacks so the open/close lifecycle effect
  // below can depend ONLY on `open`. Without this, initializeScanner/stopScanner
  // change identity on every scanStatus transition, re-running the effect while
  // the modal is open and re-showing the rationale dialog over the live scanner.
  const initializeScannerRef = useRef(initializeScanner);
  const stopScannerRef = useRef(stopScanner);
  useEffect(() => {
    initializeScannerRef.current = initializeScanner;
    stopScannerRef.current = stopScanner;
  });

  // Initialize scanner when modal opens (runs only when `open` toggles)
  useEffect(() => {
    if (open) {
      if (hasGrantedCameraRef.current) {
        // Already granted camera this session — skip rationale
        const timer = setTimeout(() => {
          initializeScannerRef.current();
        }, 100);
        return () => clearTimeout(timer);
      } else {
        // Show rationale before requesting camera
        rationaleResolvedRef.current = false;
        setShowRationale(true);
      }
    } else {
      // Reset state when closing (also aborts an in-flight charger lookup)
      stopScannerRef.current();
      setScanStatus('idle');
      setErrorMessage('');
      setPermissionDenied(false);
      setShowRationale(false);
      successResultRef.current = null;
      isProcessingRef.current = false;
    }
  }, [open]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, [stopScanner]);

  // Handle file upload (gallery image)
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setScanStatus('processing');

    try {
      // Create a temporary scanner instance for file scanning
      const html5QrCode = new Html5Qrcode('qr-file-scanner');

      const result = await html5QrCode.scanFile(file, true);

      // Clean up temporary scanner
      html5QrCode.clear();

      // Process the result
      handleScanSuccess(result);
    } catch (err: any) {
      console.error('[QRScanner] Failed to scan image:', err);
      setScanStatus('error');

      if (err?.message?.includes('No QR code') || err?.message?.includes('NotFoundException')) {
        setErrorMessage('No QR code found in the image. Please try another image.');
      } else {
        setErrorMessage('Failed to scan the image. Please try another image.');
      }

      // Reset after showing error
      setTimeout(() => {
        setScanStatus(permissionDenied ? 'error' : 'scanning');
        if (!permissionDenied) {
          setErrorMessage('');
        }
      }, 3000);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/80 z-[2000] flex items-center justify-center">
      <div className="bg-white dark:bg-gray-900 rounded-3xl w-full max-w-md mx-4 overflow-hidden max-h-[90dvh] flex flex-col">
        {/* Header */}
        <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#8bc43e]/10 rounded-xl flex items-center justify-center">
                <QrCode className="w-6 h-6 text-[#8bc43e]" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Scan QR Code</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">Scan to start charging</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 transition-all"
            >
              <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Scanner Area */}
        <div className="p-6 flex-1 min-h-0 overflow-y-auto">
          {/* Camera View */}
          <div className="relative bg-gray-900 dark:bg-black rounded-2xl overflow-hidden aspect-square max-h-[45dvh] mb-4">
            {/* Scanner container - html5-qrcode will render here */}
            <div
              ref={scannerContainerRef}
              className="absolute inset-0"
              style={{
                display: scanStatus === 'scanning' || scanStatus === 'initializing' ? 'block' : 'none',
              }}
            />

            {/* Hidden container for file scanning */}
            <div id="qr-file-scanner" style={{ display: 'none' }} />

            {/* Viewfinder frame — drawn in every state, including while scanning.
                Previously it was hidden exactly when the camera was live, leaving
                the user with only the library's overlay to aim at. */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <ScanFrame />
            </div>

            {/* Status overlay for non-scanning states */}
            {(scanStatus !== 'scanning' || permissionDenied) && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="absolute inset-0 bg-black/40" />
                <div className="relative flex items-center justify-center">
                    {scanStatus === 'idle' && (
                      <div className="text-center">
                        <Camera className="w-16 h-16 text-white/50 mx-auto mb-3" />
                        <p className="text-white/70 text-sm">Position QR code in frame</p>
                      </div>
                    )}
                    {scanStatus === 'initializing' && (
                      <div className="text-center">
                        <div className="w-16 h-16 border-4 border-[#8bc43e] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                        <p className="text-white text-sm font-semibold">Starting camera...</p>
                      </div>
                    )}
                    {scanStatus === 'processing' && (
                      <div className="text-center">
                        <div className="w-16 h-16 border-4 border-[#8bc43e] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                        <p className="text-white text-sm font-semibold">Processing...</p>
                      </div>
                    )}
                    {scanStatus === 'success' && (
                      <div className="text-center">
                        <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                          <CheckCircle2 className="w-12 h-12 text-white" />
                        </div>
                        <p className="text-green-400 text-sm font-semibold">QR Code Detected!</p>
                      </div>
                    )}
                    {scanStatus === 'error' && !permissionDenied && (
                      <div className="text-center px-4">
                        <div className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-3">
                          <XCircle className="w-12 h-12 text-white" />
                        </div>
                        <p className="text-red-400 text-sm font-semibold">Scan Failed</p>
                      </div>
                    )}
                    {permissionDenied && (
                      <div className="text-center px-4">
                        <div className="w-20 h-20 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-3">
                          <AlertTriangle className="w-12 h-12 text-white" />
                        </div>
                        <p className="text-amber-400 text-sm font-semibold">Camera Access Denied</p>
                        <p className="text-white/60 text-xs mt-2">Upload an image instead</p>
                      </div>
                    )}
                </div>
              </div>
            )}

            {/* Gradient Overlay */}
            <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/80 to-transparent pointer-events-none"></div>
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl p-4 mb-4">
              <p className="text-sm text-red-700 dark:text-red-300">{errorMessage}</p>
            </div>
          )}

          {/* Instructions */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 mb-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">How to scan:</h3>
            <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
              <li>1. Find the QR code on the charging station</li>
              <li>2. Position it within the frame above</li>
              <li>3. Hold steady until it scans automatically</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            {/* Upload from Gallery Button */}
            <Button
              onClick={handleUploadClick}
              className="w-full h-12 bg-[#8bc43e] hover:bg-[#7ab32e] text-white rounded-xl font-semibold flex items-center justify-center gap-2"
              disabled={scanStatus === 'processing' || scanStatus === 'success'}
            >
              <ImagePlus className="w-5 h-5" />
              Upload from Gallery
            </Button>

            {/* Cancel Button */}
            <Button
              onClick={handleClose}
              variant="outline"
              className="w-full h-12 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white rounded-xl font-semibold"
            >
              Cancel
            </Button>
          </div>

          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
          />
        </div>
      </div>

      {/* Camera Permission Rationale Dialog.
          Dismissing (Escape/overlay) continues to the camera rather than closing
          onto nothing: App Store guideline 5.1.1(iv) requires the permission
          request to follow the message. "Upload Instead" is a different feature,
          not a deferral, so it stays. rationaleResolvedRef keeps a button press —
          which closes the dialog itself — from firing this a second time. */}
      <AlertDialog
        open={showRationale}
        onOpenChange={(o) => {
          if (o) return;
          setShowRationale(false);
          if (!rationaleResolvedRef.current) handleRationaleAllow();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex justify-center mb-2">
              <div className="w-14 h-14 bg-[#8bc43e]/10 rounded-full flex items-center justify-center">
                <Camera className="w-7 h-7 text-[#8bc43e]" />
              </div>
            </div>
            <AlertDialogTitle className="text-center">Camera Access Needed</AlertDialogTitle>
            <AlertDialogDescription className="text-center">
              EVee Buddy needs your camera to scan QR codes on charging stations and quickly start a charging session.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleRationaleDeny}>Upload Instead</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRationaleAllow}
              className="bg-[#8bc43e] hover:bg-[#7ab32e]"
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
